import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import {
  DSBadge,
  DSCardWrapper,
  DSDropdown,
} from "@components/design-system/molecules";
import { DSNoteDisplay } from "@components/design-system/organisms";
import { faFile, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  faFileArrowUp,
  faFilePen,
  faGrid2,
  faRabbitFast,
  faStamp,
  faTurtle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTemplates } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import WizardStep from "./WizardStep";
import {
  SOAP_TEMPLATE_EXAMPLES,
  SOAP_TEMPLATE_LIBRARY_EXAMPLES,
  FREEFORM_TEMPLATE_LIBRARY_EXAMPLES,
  TEMPLATE_TYPES,
  TEMPLATE_FIELDS,
} from "../constants";
import { useLoadUserTemplate } from "../hooks/useLoadUserTemplate";
import { useTemplateExamples } from "../hooks/useTemplateExamples";
import TemplateFormButton from "../TemplateBuilder/components/common/TemplateFormButton";
import FileUploader from "../TemplateBuilder/components/FileUploader";
import {
  applyBuiltInPreset,
  buildTemplateDropdownOptions,
  resetTemplateFields,
} from "../utils";

const METHOD_TO_SUBVIEW = {
  preset: "preset-picker",
  upload: "upload",
  scratch: "type-selector",
};

export default function StartingPointStep({
  onNext,
  onFileUpload,
  loadingExtraction,
  control,
  selectedMethod,
  onMethodChange,
}) {
  // const { expandSidebar, createNewSession, creatingSession } =
  //   useCompanion();
  const { setValue } = useFormContext();
  const templateUuid = useWatch({ control, name: "uuid" });
  const currentType = useWatch({ control, name: "templateType" });
  const { ownedTemplates, sharedTemplates } = useTemplates();
  const { freeformExamples, allSoapExamples, allFreeformExamples } =
    useTemplateExamples();
  const { loadUserTemplate, isLoadingTemplate } =
    useLoadUserTemplate();

  const [subView, setSubView] = useState(
    selectedMethod
      ? METHOD_TO_SUBVIEW[selectedMethod] || "main"
      : "main",
  );
  const [selectedPresetKey, setSelectedPresetKey] = useState(null);

  const handleMethodSelect = (method) => {
    onMethodChange(method);
    mixpanel.track("Wizard Starting Point Selected", {
      templateUuid,
      method,
    });

    switch (method) {
      case "preset":
        setSubView("preset-picker");
        break;
      case "upload":
        setSubView("upload");
        break;
      case "scratch":
        setSubView("type-selector");
        break;
    }
  };

  const handlePresetSelect = async (
    presetName,
    templateType,
    option,
  ) => {
    if (option?.isUserTemplate) {
      const result = await loadUserTemplate(presetName);
      if (result) {
        setValue("templateType", result.templateType);
        setValue("templateFields", result.fields, {
          shouldDirty: true,
        });
        setValue("userPreferences", result.userPreferences);
        setValue("templateVariant", presetName);

        mixpanel.track("Wizard Preset Selected", {
          templateUuid,
          presetName: option.label,
          templateType: result.templateType,
          isUserTemplate: true,
        });

        onNext();
      }
      return;
    }

    const examples =
      templateType === TEMPLATE_TYPES.CUSTOM
        ? allFreeformExamples
        : allSoapExamples;
    const preset = examples[presetName];
    if (!preset) return;

    setValue("templateType", templateType);
    applyBuiltInPreset(setValue, preset, templateType);
    setValue("sampleTranscript", preset.sampleTranscript || "");
    setValue("templateVariant", presetName);

    mixpanel.track("Wizard Preset Selected", {
      templateUuid,
      presetName,
      templateType,
      isUserTemplate: false,
    });

    onNext();
  };

  const handleTypeSelect = (templateType) => {
    resetTemplateFields(setValue, templateType);

    mixpanel.track("Wizard Template Type Selected", {
      templateUuid,
      templateType,
      method: selectedMethod,
    });

    onNext();
  };

  // Main view: three starting point cards
  if (subView === "main") {
    return (
      <WizardStep
        title="Choose Your Starting Point"
        description="How would you like to build your template?"
        hideNext
        hideBack
        dataTestId="wizard-step-starting-point"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
          {/* <DSCardWrapper
            interactive
            disabled={creatingSession}
            onClick={() => {
              if (creatingSession) return;
              expandSidebar();
              createNewSession();
              mixpanel.track("Wizard Starting Point Selected", {
                templateUuid,
                method: "quill",
              });
            }}
            dataTestId="wizard-option-quill"
          >
            <DSBadge
              variant="green"
              dataTestId="wizard-badge-preset-pro"
              size="sm"
            >
              New!
            </DSBadge>
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <Twemoji emoji="🦔" className="text-3xl" />
              <h4 className="text-base font-semibold text-primary">
                Build With Quill
              </h4>{" "}
              <p className="text-sm text-secondary">
                Quill the Writing Companion is at your service to help
                you build your template. Just tell him what you want
                and he'll get to work!
              </p>
              <DSBadge
                icon={faRocket}
                variant="green"
                dataTestId="wizard-badge-quill"
              >
                ~2-5 min
              </DSBadge>
            </div>
          </DSCardWrapper> */}

          <DSCardWrapper
            interactive
            onClick={() => handleMethodSelect("preset")}
            dataTestId="wizard-option-preset"
          >
            <DSBadge
              variant="brand"
              dataTestId="wizard-badge-preset-pro"
              size="sm"
            >
              Recommended
            </DSBadge>
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <FontAwesomeIcon
                icon={faGrid2}
                className="text-brand text-3xl"
              />
              <h4 className="text-base font-semibold text-primary">
                Select a Preset
              </h4>{" "}
              <p className="text-sm text-secondary">
                Choose from our library of tried & tested templates
                made by our expert clinical and AI team.
              </p>
              <DSBadge
                icon={faRabbitFast}
                variant="green"
                dataTestId="wizard-badge-preset"
              >
                ~2-5 min
              </DSBadge>
            </div>
          </DSCardWrapper>

          <DSCardWrapper
            interactive
            onClick={() => handleMethodSelect("upload")}
            dataTestId="wizard-option-upload"
          >
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <FontAwesomeIcon
                icon={faFileArrowUp}
                className="text-brand text-3xl"
              />
              <h4 className="text-base font-semibold text-primary">
                Upload Your Own
              </h4>
              <p className="text-sm text-secondary">
                Upload a PDF of your own template or a completed note,
                and we'll identify the sections, formatting, and
                structure to create a template our AI can use.
              </p>
              <DSBadge
                icon={faRabbitFast}
                variant="green"
                dataTestId="wizard-badge-upload"
              >
                ~2-5 min
              </DSBadge>
            </div>
          </DSCardWrapper>

          <DSCardWrapper
            interactive
            onClick={() => handleMethodSelect("scratch")}
            dataTestId="wizard-option-scratch"
          >
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <FontAwesomeIcon
                icon={faFilePen}
                className="text-brand text-3xl"
              />
              <h4 className="text-base font-semibold text-primary">
                Start from Scratch
              </h4>
              <p className="text-sm text-secondary">
                Are you a template aficionado? Build your template
                step by step with full control over every detail.
              </p>
              <DSBadge
                icon={faTurtle}
                variant="yellow"
                dataTestId="wizard-badge-scratch"
              >
                ~5-10 min
              </DSBadge>
            </div>
          </DSCardWrapper>
        </div>
        {/* <div className="flex justify-center mt-md text-base gap-sm">
          <DSLinkButton
            onClick={() => handleMethodSelect("scratch")}
            dataTestId="wizard-option-scratch"
          >
            Or, Start from Scratch
          </DSLinkButton>
          <DSBadge variant="yellow" dataTestId="wizard-badge-scratch">
            ~5-10 min
          </DSBadge>
        </div> */}
      </WizardStep>
    );
  }

  // Preset picker sub-view
  if (subView === "preset-picker") {
    const defaultOptions = [
      ...Object.values(SOAP_TEMPLATE_EXAMPLES).map((t) => ({
        label: t.name,
        value: t.name,
      })),
      ...Object.values(freeformExamples).map((t) => ({
        label: t.name,
        value: t.name,
      })),
    ];

    const libraryOptions = [
      ...Object.values(SOAP_TEMPLATE_LIBRARY_EXAMPLES).map((t) => ({
        label: t.name,
        value: t.name,
      })),
      ...Object.values(FREEFORM_TEMPLATE_LIBRARY_EXAMPLES).map(
        (t) => ({
          label: t.name,
          value: t.name,
        }),
      ),
    ];

    const myTemplateOptions = ownedTemplates
      .filter((t) => t.uuid !== templateUuid)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        label: t.name,
        value: t.uuid,
        isUserTemplate: true,
      }));

    const sharedTemplateOptions = sharedTemplates
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        label: t.name,
        value: t.uuid,
        isUserTemplate: true,
      }));

    const dropdownOptions = buildTemplateDropdownOptions({
      defaultOptions,
      libraryOptions,
      myTemplateOptions,
      sharedTemplateOptions,
    });

    const selectedOption = selectedPresetKey
      ? dropdownOptions.find((o) => o.value === selectedPresetKey)
      : null;
    const isUserTemplate = selectedOption?.isUserTemplate;

    const selectedPreset =
      !isUserTemplate && selectedPresetKey
        ? allSoapExamples[selectedPresetKey] ||
          allFreeformExamples[selectedPresetKey]
        : null;
    const selectedType = selectedPresetKey
      ? isUserTemplate
        ? null
        : allSoapExamples[selectedPresetKey]
          ? TEMPLATE_TYPES.SOAP
          : TEMPLATE_TYPES.CUSTOM
      : null;

    const noteDisplayData = selectedPreset
      ? selectedType === TEMPLATE_TYPES.SOAP
        ? {
            subjective:
              selectedPreset[TEMPLATE_FIELDS.SUBJECTIVE]?.trim() ||
              undefined,
            objective:
              selectedPreset[TEMPLATE_FIELDS.OBJECTIVE]?.trim() ||
              undefined,
            assessment:
              selectedPreset[TEMPLATE_FIELDS.ASSESSMENT]?.trim() ||
              undefined,
            plan:
              selectedPreset[TEMPLATE_FIELDS.PLAN]?.trim() ||
              undefined,
          }
        : {
            content:
              selectedPreset[TEMPLATE_FIELDS.CUSTOM]?.trim() ||
              undefined,
          }
      : null;

    return (
      <WizardStep
        title="Select a Preset Template"
        description="Search for a template that most closely matches your needs. You can customize it in the next step."
        onBack={() => {
          setSelectedPresetKey(null);
          setSubView("main");
        }}
        onNext={() =>
          handlePresetSelect(
            selectedPresetKey,
            selectedType,
            selectedOption,
          )
        }
        nextDisabled={!selectedPresetKey || isLoadingTemplate}
        nextLabel={isLoadingTemplate ? "Loading..." : "Next"}
        dataTestId="wizard-step-preset-picker"
      >
        <div className="flex flex-col gap-lg">
          <DSDropdown
            options={dropdownOptions}
            value={selectedPresetKey}
            onChange={(value) => setSelectedPresetKey(value)}
            placeholder="Choose a preset template..."
            search
            searchPlaceholder="Search templates..."
            width="w-full"
            maxHeight={400}
            dataTestId="wizard-preset-dropdown"
            leadingIcon={faStamp}
            disabled={isLoadingTemplate}
            getOptionTestId={(option) =>
              option.isHeader
                ? `wizard-preset-header-${option.value}`
                : `wizard-preset-${option.value}`
            }
          />

          {selectedPreset && noteDisplayData && (
            <div className="flex flex-col gap-md">
              <div className="flex items-center gap-sm">
                <h5>{selectedPreset.name}</h5>
                <DSBadge
                  variant={
                    selectedType === TEMPLATE_TYPES.SOAP
                      ? "brand"
                      : "blue"
                  }
                  size="sm"
                  dataTestId="wizard-preset-preview-badge"
                >
                  {selectedType === TEMPLATE_TYPES.SOAP
                    ? "SOAP"
                    : "Freeform"}
                </DSBadge>
                <p>{selectedPreset.description}</p>
              </div>

              <DSNoteDisplay
                data={noteDisplayData}
                variant={
                  selectedType === TEMPLATE_TYPES.SOAP
                    ? "soap"
                    : "freeform"
                }
                layout="stacked"
                size="compact"
                showCopyButton={false}
                showAdditionalNotes={false}
                dataTestId="wizard-preset-preview"
              />

              {selectedPreset.preferences && (
                <DSCardWrapper variant="ghost">
                  <span className="text-sm font-semibold text-brand">
                    Preferences
                  </span>
                  <p className="text-xs text-secondary mt-xs line-clamp-2">
                    {selectedPreset.preferences}
                  </p>
                </DSCardWrapper>
              )}
            </div>
          )}

          {isUserTemplate && selectedPresetKey && (
            <div className="flex items-center gap-sm">
              <h5>{selectedOption?.label}</h5>
              <p className="text-sm text-secondary">
                Click Next to load this template as your starting
                point.
              </p>
            </div>
          )}
        </div>
      </WizardStep>
    );
  }

  // Upload sub-view
  if (subView === "upload") {
    const handleUploadTypeChange = (e) => {
      const newType = e.target.value;
      resetTemplateFields(setValue, newType);

      mixpanel.track("Wizard Template Type Selected", {
        templateUuid,
        templateType: newType,
        method: selectedMethod,
      });
    };

    return (
      <WizardStep
        title="Upload Your Template"
        description="Upload a PDF of your existing template or a completed note. Select the format first."
        onBack={() => setSubView("main")}
        onNext={onNext}
        nextDisabled={loadingExtraction}
        nextLabel={loadingExtraction ? "Extracting..." : "Next"}
        dataTestId="wizard-step-upload"
      >
        <div className="flex flex-col gap-md">
          <div>
            <label className="block text-sm font-semibold text-primary mb-sm">
              Template Format
            </label>
            <div className="flex gap-sm">
              <TemplateFormButton
                icon={faSoapNote}
                title="SOAP"
                description="This will generate a note in separate Subjective, Objective, Assessment, and Plan sections."
                isSelected={currentType === TEMPLATE_TYPES.SOAP}
                name="templateType"
                value={TEMPLATE_TYPES.SOAP}
                checked={currentType === TEMPLATE_TYPES.SOAP}
                onChange={handleUploadTypeChange}
                dataTestId="wizard-upload-type-soap"
              />
              <TemplateFormButton
                icon={faFile}
                title="Freeform"
                description="This will generate a note in a single text box, formatted however you specify in your template and preferences."
                isSelected={currentType === TEMPLATE_TYPES.CUSTOM}
                name="templateType"
                value={TEMPLATE_TYPES.CUSTOM}
                checked={currentType === TEMPLATE_TYPES.CUSTOM}
                onChange={handleUploadTypeChange}
                dataTestId="wizard-upload-type-freeform"
              />
            </div>
          </div>

          {loadingExtraction ? (
            <div
              className="text-center my-md flex flex-col items-center gap-sm border-2 border-dashed rounded-large p-md border-brand"
              data-testid="wizard-extracting-spinner"
            >
              <span>Extracting the text from your PDF...</span>
              <FontAwesomeIcon
                icon={faSpinner}
                size="2x"
                className="animate-spin text-brand"
              />
            </div>
          ) : (
            <FileUploader
              onFileUpload={async (file) => {
                const success = await onFileUpload(file);
                if (success) onNext();
              }}
              label="Upload a PDF"
              helperText="Drag and drop or click to select a PDF file (max 10MB)"
              dataTestId="wizard-upload-dropzone"
            />
          )}
        </div>
      </WizardStep>
    );
  }

  // Type selector sub-view (scratch path)
  if (subView === "type-selector") {
    return (
      <WizardStep
        title="Choose Template Format"
        description="Select how you'd like your notes to be structured."
        onBack={() => setSubView("main")}
        hideNext
        dataTestId="wizard-step-type-selector"
      >
        <div className="flex gap-sm">
          <TemplateFormButton
            icon={faSoapNote}
            title="SOAP"
            description="This will generate a note in separate Subjective, Objective, Assessment, and Plan sections."
            isSelected={currentType === TEMPLATE_TYPES.SOAP}
            name="templateType"
            value={TEMPLATE_TYPES.SOAP}
            checked={currentType === TEMPLATE_TYPES.SOAP}
            onChange={() => {}}
            onClick={() => handleTypeSelect(TEMPLATE_TYPES.SOAP)}
            dataTestId="wizard-type-soap"
          />
          <TemplateFormButton
            icon={faFile}
            title="Freeform"
            description="This will generate a note in a single text box, formatted however you specify in your template and preferences."
            isSelected={currentType === TEMPLATE_TYPES.CUSTOM}
            name="templateType"
            value={TEMPLATE_TYPES.CUSTOM}
            checked={currentType === TEMPLATE_TYPES.CUSTOM}
            onChange={() => {}}
            onClick={() => handleTypeSelect(TEMPLATE_TYPES.CUSTOM)}
            dataTestId="wizard-type-freeform"
          />
        </div>
      </WizardStep>
    );
  }

  return null;
}
